<script lang="ts">
    import {SHOW_LOC_MESSAGES_LANGUAGE} from 'shared/providers';
    import {KpPopoverPanel, type TogglePopoverFn} from 'shared/ui-widgets/popover';
    import {getCurrentLanguage, getLogger} from 'src/core';
    import type {DateValueEditorLocalizations, DateValueEditorOptions, DateValueEditorValidations} from './types';
    import type {EditorsLocalizationFunction} from '../../../localizations/types';
    import type {FormControlOptions} from '../../forms/types';
    import type {ValueEditorSize} from '../../../types';
    import type {FormControl} from '../../forms/form-control';
    import {focusOnMount} from '../../shared/use.focus-on-mount';
    import {formControls} from '../../forms/use.form-controls';
    import {touched} from '../../shared/use.touched';
    import {style} from 'svelte-forms';
    import {ariaInvalid} from '../../errors/use.aria-invalid';
    import {ariaErrormessage} from '../../errors/use.aria-errormessage';
    import {ariaDescribedby} from '../../description/use.aria-describedby';
    import {getContext} from 'svelte';
    import {FormControlBuilder} from '../../forms/form-control';
    import {createInputDescription} from '../../description/utils';
    import {onDestroy} from 'svelte';
    import {onlyDateFormatter, onlyDateParser, validatorFactory} from './utils';
    import {getDateFormatter, getDateParser} from '../_shared/date-time-utils';
    import {DateTime, Info} from 'luxon';
    import {required} from '../../forms/use.required';
    import {createFloatingActions} from 'svelte-floating-ui';
    import {floatingUiCommonSettings} from 'shared/utils/floating-ui-common-settings';
    import {isFunction, uuid} from 'shared/utils/custom-utils';
    import SveltyPicker from 'svelty-picker';
    import {EDITORS_LOCALIZE} from '../../../context-keys';
    import ErrorMessages from '../../errors/ErrorMessages.svelte';
    import Description from '../../description/Description.svelte';
    import KpButton from 'shared/ui-widgets/button/KpButton.svelte';
    import IconedContent from 'shared/ui-widgets/uicons/IconedContent.svelte';

    export let model: string;
    export let formControlOptions: FormControlOptions;

    export let editorId: string;
    export let editorName: string;
    export let placeholder: string;

    export let isDisabled: boolean;
    export let isFocused: boolean;
    export let forceShowErrors: boolean;
    export let size: ValueEditorSize;

    export let options: DateValueEditorOptions;
    export let validations: DateValueEditorValidations;

    const currentLanguage = getCurrentLanguage();
    const localize: EditorsLocalizationFunction<DateValueEditorLocalizations> = getContext(EDITORS_LOCALIZE);

    const formControl = FormControlBuilder.for(editorId, editorName, model)
        .withValidators(validatorFactory(validations))
        .withOptions(formControlOptions)
        .withParsers([(value: string) => value.trim(), getDateParser({customViewFormat: options.viewFormat})])
        .withModifiers([onlyDateParser(options)])
        .withFormatters([getDateFormatter({customViewFormat: options.viewFormat}), onlyDateFormatter(options)])
        .build(getLogger());

    const subscription = formControl.getModelValue$().subscribe((value) => model = value);  // two-way data binding -> sending updated value up to a parent
    $: formControl.setModelValue(model); // two-way data binding -> receiving new model value from parent

    onDestroy(() => {
        subscription.unsubscribe();
    });

    const inputDescription = createInputDescription({editorId, editorName, description: localize('description')});

    const datetimePickerLocalization = (!!currentLanguage && currentLanguage !== SHOW_LOC_MESSAGES_LANGUAGE) ? createLocalizationForLocale(currentLanguage) : undefined;

    const popoverId = uuid();
    let togglePopover: TogglePopoverFn;
    const [floatingRef, floatingContent] = createFloatingActions(floatingUiCommonSettings('bottom-end'));

    let inputElement: HTMLElement;

    function onPickerChange(event: CustomEvent<string>, pickerCloseHandler: () => void) {
        formControl.setViewValue(event.detail ?? '');
        pickerCloseHandler();
        inputElement.focus();
    }

    function createLocalizationForLocale(locale: string) {
        const copyLastElementToFront = (array: string[]) => {
            array.unshift(array.at(-1));
            return array;
        };

        return {
            cancelBtn: undefined,
            okBtn: undefined,
            clearBtn: localize('clearBtn'),
            todayBtn: localize('todayBtn'),
            timeView: localize('timeView'),
            backToDate: localize('backToDate'),
            days: copyLastElementToFront(Info.weekdays('long', {locale})), // weekday localization start at 0 with Sunday and ends at 7 also with sunday
            daysShort: copyLastElementToFront(Info.weekdays('short', {locale})), // weekday localization start at 0 with Sunday and ends at 7 also with sunday
            daysMin: copyLastElementToFront(Info.weekdays('short', {locale})), // weekday localization start at 0 with Sunday and ends at 7 also with sunday
            months: Info.months('long', {locale}),
            monthsShort: Info.months('short', {locale}),
            meridiem: Info.meridiems({locale}),
            suffix: ['st', 'nd', 'rd', 'th']
        };
    }

    export function getFormController(): FormControl<string> {
        return formControl;
    }

    const handleInputFocus = () => {
        setTimeout(() => {
            if (isFunction(togglePopover)) {
                togglePopover(true);
            }
        }, 250);
    }
</script>

<div class="input-group input-group-{size}">
    <input class="form-control input-{size}"
           type="text"
           id={editorId}
           name={editorName}
           {placeholder}
           disabled={isDisabled}
           on:focus={handleInputFocus}
           use:required={validations.required}
           use:focusOnMount={isFocused}
           use:formControls={formControl}
           use:touched={{formControl}}
           use:style={{field: formControl.getFieldStateAsStore()}}
           use:ariaInvalid={formControl}
           use:ariaErrormessage={formControl}
           use:ariaDescribedby={inputDescription}

           bind:this={inputElement}
           data-main-input/>

    <span class="input-group-btn">
        <KpButton use={[floatingRef]} {isDisabled} buttonSize={size} popovertarget={popoverId}>
            <IconedContent icon="calendar">
                <span class="sr-only">{localize('openCalendar')}</span>
            </IconedContent>
        </KpButton>
    </span>
</div>

<KpPopoverPanel id={popoverId}
                use={[floatingContent]}
                additionalClasses="datetime-picker-popover"
                on:popover-close={() => formControl.setTouched()}
                bind:togglePopover>

    <div class="datetime-picker-container">
        <SveltyPicker inputClasses="hidden"
                      inputId="{editorId}-picker"
                      name="{editorName}-picker"
                      format={options.maximumGranularity === 'date-only' ? 'd.m.yyyy' : 'd.m.yyyy h:ii'}
                      initialDate={model ? DateTime.fromISO(model).toJSDate() : null}
                      startDate={validations.minDate ? DateTime.fromISO(validations.minDate).toJSDate() : null}
                      endDate={validations.maxDate ? DateTime.fromISO(validations.maxDate).toJSDate() : null}
                      i18n={datetimePickerLocalization}
                      disabled={isDisabled}
                      pickerOnly={true}
                      todayBtn={true}
                      clearBtn={true}
                      on:change={(event) => {onPickerChange(event, togglePopover)}}>
        </SveltyPicker>
    </div>
</KpPopoverPanel>

<ErrorMessages formController={formControl} {forceShowErrors} {size}/>
<Description {inputDescription}/>

<style lang="less">
    @import (reference) "styles/portaro.variables.less";
    @import (reference) "styles/portaro.themes.less";

    :global {
        .datetime-picker-popover {
            --popover-content-padding: 0;
        }
    }

    .datetime-picker-container {
        --sdt-wrap-shadow: none;
        --sdt-table-selected-bg: var(--accent-blue);
        --sdt-color-selected: #fff;
        --sdt-bg-selected: var(--brand-orange);
        --sdt-today-bg: var(--brand-orange);
        --sdt-today-color: #fff;
        --sdt-clear-bg: var(--danger-red);
        --sdt-clear-color: #fff;

        :global {
            // Custom styles for nicer SveltyPicker comp styling
            .std-calendar-wrap {
                padding: 0;

                .sdt-thead-nav {
                    padding: @spacing-s @spacing-m;
                    margin-bottom: @spacing-s;
                    border-bottom: 1px solid @themed-border-default;

                    .std-btn:nth-child(2),
                    .std-btn:nth-child(3) {
                        position: relative;
                        font-family: uicons-bold-rounded !important;
                        font-style: normal;
                        font-weight: normal !important;
                        font-variant: normal;
                        text-transform: none;
                        line-height: 1;
                        -webkit-font-smoothing: antialiased;
                        -moz-osx-font-smoothing: grayscale;

                        --sdt-header-color: #fff;

                        &:before {
                            position: absolute;
                            top: 50%;
                            left: 50%;
                            transform: translate(-50%, -50%);
                            color: @themed-text-default;
                        }

                        .sdt-svg {
                            opacity: 0;
                        }
                    }

                    .std-btn:nth-child(2):before {
                        content: '\fd2c';
                    }

                    .std-btn:nth-child(3):before {
                        content: '\fd2f';
                    }
                }

                .sdt-calendar {
                    padding: 0 @spacing-s;
                    margin-bottom: 2px;
                }

                .sdt-btn-row {
                    padding: @spacing-s @spacing-m;
                    border-top: 1px solid @themed-border-default;
                    display: flex;
                    justify-content: end;

                    .sdt-today-btn,
                    .sdt-clear-btn {
                        height: 32px;
                        padding: 0 @spacing-m;
                        border-radius: @border-radius-default;
                        overflow: hidden;
                        position: relative;
                        transition: background-color 0.2s ease-in-out;
                        border: 1px solid rgba(0, 0, 0, 0.2);

                        &:after {
                            content: '';
                            z-index: 1;
                            outline: none;
                            border: none;
                            position: absolute;
                            top: 0;
                            left: 0;
                            right: 0;
                            bottom: 0;
                            width: 100%;
                            height: 100%;
                            opacity: 0;
                            background-color: rgba(0, 0, 0, 0.2);
                            transition: opacity 0.3s ease-in-out;
                        }
                    }

                    .sdt-today-btn:hover {
                        background-color: var(--brand-orange);

                        &:after {
                            opacity: 1;
                        }
                    }

                    .sdt-clear-btn:hover {
                        background-color: var(--danger-red);

                        &:after {
                            opacity: 1;
                        }
                    }
                }
            }
        }
    }
</style>